import React, {createRef, useContext, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {useQueryClient} from "react-query";
import axios from "axios";
import __ from "lodash";

import {ButtonBase, Card, CardMedia, Grid, CardContent, IconButton, useTheme} from "@material-ui/core";
import {
  Build, Schedule, MoreVert, AirplanemodeInactiveTwoTone, AirplanemodeActiveTwoTone, TrackChanges, Link
} from "@material-ui/icons";

import {AircraftMaintenance as  AircraftMaintenanceIcon, AirplaneMxIcon} from "../../../icons";
import IconButtonMenu from "../../../controls/IconButtonMenu";
import AircraftStatus from "./AircraftStatus";
import AircraftMovements from "../AircraftMovements";
import AircraftMaintenance from "../AircraftMaintenance";
import AircraftScheduledMaintenance from "../AircraftScheduledMaintenance";
import AircraftMovementCaptureDialog from "./AircraftMovementCaptureDialog";
import AircraftDefectsDialog from "./AircraftDefectsDialog";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import SystemContext from "../../../SystemContext";

import {useAircraft} from "../../../services/aircraft";
import AirplaneClockCancelIcon from "../../../icons/AirplaneClockCancelIcon";
import AircraftDeleteLastMovementDialog from "./AircraftDeleteLastMovementDialog";
import {useAuth0} from "@auth0/auth0-react";

const AccessControl = require("../../../AccessControl");
const apiSitePrefix = getApiSitePrefix();

const useStyles = makeStyles((theme) => ({
  root: {
    height: '75vh',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  gridList: {
    width: 1000,
    height: 450,
  },
  icon: {
    color: 'rgba(255, 255, 255, 0.54)',
  },
  externalIcon: {
    // color: 'rgba(252, 91, 3, 1)',
    color: theme.palette.externalCard.backgroundColor,
  },
  titleBar: {
    background:
      'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
  },
  card: {
    padding: 6,
    minWidth: 150,
    maxWidth: 150,
    borderRadius: 12,
    position: 'relative',
    backgroundColor: theme.palette.card.backgroundColor,
    "& .cardContent": {
      borderRadius: 6,
      padding: "0px 4px 0px 4px",
      backgroundColor: theme.palette.card.content.backgroundColor,
    },
    "& .cardTitle": {
      fontSize:25,
      fontWeight:800,
      fontFamily:"Ubuntu, sans-serif",
      fontVariantCaps: "small-caps",
      verticalAlign: "middle",
      marginTop:10,
      marginLeft:-5,
      color: theme.palette.card.content.contrastText
    }
  },
  aogCard: {
    padding: 6,
    minWidth: 150,
    maxWidth: 150,
    borderRadius: 12,
    position: 'relative',
    backgroundColor: theme.palette.aogCard.backgroundColor,
    "& .cardContent": {
      borderRadius: 6,
      padding: "0px 4px 0px 4px",
      backgroundColor: theme.palette.aogCard.content.backgroundColor,
    },
    "& .cardTitle": {
      fontSize:25,
      fontWeight:800,
      fontFamily:"Ubuntu, sans-serif",
      fontVariantCaps: "small-caps",
      verticalAlign: "middle",
      marginTop:10,
      marginLeft:-5,
      color: theme.palette.aogCard.content.contrastText
    }
  },
  scheduledCard: {
    padding: 6,
    minWidth: 150,
    maxWidth: 150,
    borderRadius: 12,
    position: 'relative',
    backgroundColor: theme.palette.maintenanceCard.backgroundColor,
    "& .cardContent": {
      borderRadius: 6,
      padding: "0px 4px 0px 4px",
      backgroundColor: theme.palette.maintenanceCard.content.backgroundColor,
    },
    "& .cardTitle": {
      fontSize:25,
      fontWeight:800,
      fontFamily:"Ubuntu, sans-serif",
      fontVariantCaps: "small-caps",
      verticalAlign: "middle",
      marginTop:10,
      marginLeft:-5,
      color: theme.palette.maintenanceCard.content.contrastText
    }
  },
  externalCard: {
    padding: 6,
    minWidth: 150,
    maxWidth: 150,
    borderRadius: 12,
    position: 'relative',
    backgroundColor: theme.palette.externalCard.backgroundColor,
    "& .cardContent": {
      borderRadius: 6,
      padding: "0px 4px 0px 4px",
      backgroundColor: theme.palette.externalCard.content.backgroundColor,
    },
    "& .cardTitle": {
      fontSize:25,
      fontWeight:800,
      fontFamily:"Ubuntu, sans-serif",
      fontVariantCaps: "small-caps",
      verticalAlign: "middle",
      marginTop:10,
      marginLeft:-5,
      color: theme.palette.externalCard.content.contrastText
    }
  },
  cardContent: {
    borderRadius: 6,
    padding: "0px 4px 0px 4px",
    backgroundColor: '#f8f8fa',
  },
  statusContent: {
    padding: "6px",
  },
  media: {
    height: 180,
    paddingTop: '56.25%', // 16:9
  },
  cardTitle: {
    fontSize:25,
    fontWeight:800,
    fontFamily:"Ubuntu, sans-serif",
    fontVariantCaps: "small-caps",
    //width: "100%",
    //alignContent: "center",
    verticalAlign: "middle",
    marginTop:10,
    marginLeft:-5
  },
  selected: {
    backgroundColor: "primary",
  },
  DefectsEditor: {
    "& .MuiTableHead-root": {
      backgroundColor: theme.palette.childTableHeader.backgroundColor,
      display: "block",
      tableLayout: "fixed",
      paddingRight: 20,
    },
    "& .MuiTableSortLabel-root": {
      color: theme.palette.secondary.main,
    }
  },
}));


const AircraftGrid = (props) => {

  const theme = useTheme();
  const classes = useStyles();
  const SystemData = useContext(SystemContext);
  const { isAuthenticated, user } = useAuth0();
  const canDelete = AccessControl.userHasAccess(user, ["Super Admin", "Admin"]);

  const tableRef = createRef({state: {lastEditingRow: false}});
  const queryClient = useQueryClient();
  const extraKeys = ['active'];
  const aircraft = useAircraft({tableRef, queryClient, extraKeys});

  const aogColor = theme?.palette?.aogCard?.content?.contrastText || "#9A0303";
  const maintenanceColor = theme?.palette?.maintenanceCard?.content?.contrastText || "#815B2C";
  const flyingColor = theme?.palette?.card?.content?.contrastText || "#f7c024";

  const [movementsDialog, setMovementsDialog] = useState({
    showMovements: false,
    aircraft: "",
  });
  const [maintenanceDialog, setMaintenanceDialog] = useState({
    showMaintenance: false,
    aircraft: "",
  });
  const [scheduledMaintenanceDialog, setScheduledMaintenanceDialog] = useState({
    showMaintenance: false,
    aircraft: "",
  });
  const [captureMovementDialog, setCaptureMovementDialog] = useState({
    captureMovement: false,
    aircraft: "",
  });
  const [deleteLastMovementDialog, setDeleteLastMovementDialog] = useState({
    deleteMovement: false,
    aircraft: "",
  });
  const [deferredDefects, setDeferredDefects] = useState({
    showDeferredDefects: false,
    aircraft: "",
  });


  const listActionOptions = [
    {action: "Scheduled Maintenance", itemLabel: "Scheduled Maintenance", itemIcon: <Schedule/>, historySuffix: "", display: "always"},
    {action: "Maintenance Status", itemLabel: "Maintenance Status", itemIcon: <Build/>, historySuffix: "", display: "always"},
    {action: "Deferred Defects", itemLabel: "Deferred Defects", itemIcon: <AirplaneMxIcon/>, historySuffix: "", display: "always"},
    {action: "Report AOG", itemLabel: "Report AOG", itemIcon: <AirplanemodeInactiveTwoTone/>, historySuffix: "", display: "non-aog"},
    {action: "AOG Recovered", itemLabel: "AOG Recovered", itemIcon: <AirplanemodeActiveTwoTone/>, historySuffix: "", display: "aog"},
    {action: "Capture Movement", itemLabel: "Capture Movement", itemIcon: <TrackChanges/>, historySuffix: "", display: "always"},
  ];

  if (canDelete)
    listActionOptions.push({action: "Delete Last Movement", itemLabel: "Delete Last Movement", itemIcon: <AirplaneClockCancelIcon/>, historySuffix: "", display: "always"});

  const handleCardMenuClick = (aircraftTail) => (event) => {
    setScheduledMaintenanceDialog({showMaintenance: true, aircraft: aircraftTail});
  }

  const handleCardClick = (aircraftTail, external) => ()  => {
    if (external) return;
    //console.log(aircraftTail);
    setMovementsDialog({showMovements: true, aircraft: aircraftTail});
  }

  const setAircraftOnGround = async (aircraftTail, action) => {
    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};


    try {
      const response = await axios.post(
        `${apiSitePrefix}/scheduledmaintenance/${action}/${aircraftTail}`,
        { },
        { headers }
      );

      //console.error(response);
      if (response && response.data) {
        if (response.data.status) {
          SystemData.openAlert("AOG", "Aircraft on Ground status set");
          queryClient.invalidateQueries(['Aircrafts', ...extraKeys]);
          queryClient.invalidateQueries(['AircraftMovementsList', aircraftTail]);
        } else {
          SystemData.openAlert("AOG", "Could not set AOG status with this error: " + response.data.error);
        }
      } else {
        SystemData.openAlert("AOG", "Could not set AOG status due to an unknown error");
      }
    } catch (err) {
      console.error(err);
      SystemData.openAlert("AOG", "Could not set AOG status with this error: " + err.message);
    }
  }

  const executeListAction = async (params) => {
    if (!params.aircraftTailNumber)
      return;
    if (params) {
      switch (params.selectedOption) {
        case "Scheduled Maintenance":
          setScheduledMaintenanceDialog({showMaintenance: true, aircraft: params.aircraftTailNumber});
          break;
        case "Maintenance Status":
          setMaintenanceDialog({showMaintenance: true, aircraft: params.aircraftTailNumber});
          break;
        case "Deferred Defects":
          setDeferredDefects({showDeferredDefects: true, aircraft: params.aircraftTailNumber});
          break;
        case "Report AOG":
          await setAircraftOnGround(params.aircraftTailNumber, "addAog");
          break;
        case "AOG Recovered":
          await setAircraftOnGround(params.aircraftTailNumber, "removeAog");
          break;
        case "Capture Movement":
          setCaptureMovementDialog({captureMovement: true, aircraft: params.aircraftTailNumber});
          break;
        case "Delete Last Movement":
          setDeleteLastMovementDialog({deleteMovement: true, aircraft: params.aircraftTailNumber});
          break;
        default:
          break;
      }
    }
  }


  if (aircraft && aircraft.data) {
    return (
      <Grid
        container
        spacing={3}
        direction="column"
        justifyContent="flex-start"
        alignItems="center"
        wrap="nowrap"
      >
        {
          aircraft.data.map((acft) => {
            let actionItems = listActionOptions;
            if (acft.activeMaintenance) {
              actionItems = __.filter(listActionOptions, function(o) { return ! (o.display === "non-aog") });
            } else {
              actionItems = __.filter(listActionOptions, function(o) { return ! (o.display === "aog") });
            }
            return (
              <Grid item xs key={acft.aircraftTailNumber}>
                <Card className={acft.activeMaintenance ? classes.aogCard : acft.external ? classes.externalCard : acft.scheduledMaintenance ? classes.scheduledCard : classes.card}>
                  <CardContent className={"cardContent"}>
                    {/*<div style={{display: "flex", marginLeft: -15}}>*/}
                    <div style={{display: "flex", marginLeft: -10 }}>
                      {/*<IconButton aria-label={acft.aircraftTailNumber} color="primary" onClick={handleCardMenuClick(acft.aircraftTailNumber)} >*/}
                      {/*  <MoreVertIcon style={{ fontSize: 25 }}/>*/}
                      {/*  /!*<SvgIcon fontSize={"small"}>*!/*/}
                      {/*  /!*  <AircraftMaintenance width={48} height={48} />*!/*/}
                      {/*  /!*</SvgIcon>*!/*/}
                      {/*</IconButton>*/}
                      {acft.external
                        ?
                          <div>
                            <Link className={classes.externalIcon} style={{ fontSize: 32, marginTop: 10, marginLeft: 8, marginRight: 8 }}/>
                          </div>
                        :
                          <IconButtonMenu
                            icon={<MoreVert style={{ fontSize: 25 }}/>}
                            // size={"small"}
                            offset={-1}
                            style={{ color: `${acft.activeMaintenance ? aogColor : acft.scheduledMaintenance ? maintenanceColor : flyingColor}` }}
                            tooltip={"Maintenance Details"}
                            menuItems={actionItems}
                            callback={executeListAction}
                            callbackParams={{aircraftTailNumber: acft.aircraftTailNumber}}
                          />
                      }
                      <div className={"cardTitle"}>
                        {acft.aircraftTailNumber}
                      </div>
                    </div>
                  </CardContent>
                  {/*<CardHeader*/}
                  {/*  action={*/}
                  {/*    <IconButton aria-label={tile.title}>*/}
                  {/*      <MoreVertIcon />*/}
                  {/*    </IconButton>*/}
                  {/*  }*/}
                  {/*  title={tile.title}*/}
                  {/*  // subheader="September 14, 2016"*/}
                  {/*/>*/}
                  <ButtonBase
                    menuid={acft.aircraftUuid}
                    onClick={handleCardClick(acft.aircraftTailNumber, acft.external)}
                  >
                    <CardMedia
                      //component="img"
                      alt={acft.aircraftTailNumber}
                      height="130"
                      image={`images/${acft.aircraftTailNumber}.jpg`}
                      title={acft.aircraftTailNumber}
                    />
                    <CardContent className={classes.statusContent}>
                      <AircraftStatus
                        aircraftTailNumber={acft.aircraftTailNumber}
                        external={acft.external || false}
                        displayLastMovement
                        movement={acft.movement}
                        height={40}
                        width={40}
                        marginLeft={10}
                        marginRight={10}
                        fontSize={18}
                        //backgroundStyle={{fill: "#f0f0f0"}}
                        //offStyle={{fill: "#e0e0e0"}}
                      />
                    </CardContent>
                  </ButtonBase>
                  {movementsDialog.showMovements && movementsDialog.aircraft === acft.aircraftTailNumber
                    ? <AircraftMovements
                        title={movementsDialog.aircraft + " Movements"}
                        open={movementsDialog.showMovements}
                        aircraftTailNumber={movementsDialog.aircraft}
                        onChange={() => {
                          setMovementsDialog({showMovements: false, aircraft: ""});
                        }}
                      />
                    : <></>
                  }
                  {scheduledMaintenanceDialog.showMaintenance && scheduledMaintenanceDialog.aircraft === acft.aircraftTailNumber
                    ? <AircraftScheduledMaintenance
                      title={scheduledMaintenanceDialog.aircraft + " Scheduled Maintenance"}
                      open={scheduledMaintenanceDialog.showMaintenance}
                      aircraftTailNumber={scheduledMaintenanceDialog.aircraft.replace("-","")}
                      onChange={() => {
                        setScheduledMaintenanceDialog({showMaintenance: false, aircraft: ""});
                      }}
                    />
                    : <></>
                  }
                  {maintenanceDialog.showMaintenance && maintenanceDialog.aircraft === acft.aircraftTailNumber
                    ? <AircraftMaintenance
                      title={maintenanceDialog.aircraft + " Maintenance Requirements"}
                      open={maintenanceDialog.showMaintenance}
                      aircraftTailNumber={maintenanceDialog.aircraft}
                      onChange={() => {
                        setMaintenanceDialog({showMaintenance: false, aircraft: ""});
                      }}
                    />
                    : <></>
                  }
                  {captureMovementDialog.captureMovement && captureMovementDialog.aircraft === acft.aircraftTailNumber
                    ? <AircraftMovementCaptureDialog
                      title={"Capture Movement for " + captureMovementDialog.aircraft}
                      open={captureMovementDialog.captureMovement}
                      aircraftTailNumber={captureMovementDialog.aircraft}
                      onChange={() => {
                        setCaptureMovementDialog({captureMovement: false, aircraft: ""});
                      }}
                    />
                    : <></>
                  }
                  {deleteLastMovementDialog.deleteMovement && deleteLastMovementDialog.aircraft === acft.aircraftTailNumber
                    ? <AircraftDeleteLastMovementDialog
                      title={"Delete Last Movement for " + deleteLastMovementDialog.aircraft}
                      open={deleteLastMovementDialog.deleteMovement}
                      aircraftTailNumber={deleteLastMovementDialog.aircraft}
                      onChange={() => {
                        setDeleteLastMovementDialog({deleteMovement: false, aircraft: ""});
                      }}
                    />
                    : <></>
                  }
                  {deferredDefects.showDeferredDefects && deferredDefects.aircraft === acft.aircraftTailNumber
                    ? <AircraftDefectsDialog
                      open={deferredDefects.showDeferredDefects}
                      record={{aircraftTailNumber: deferredDefects.aircraft}}
                      title={"Deferred Defects for " + deferredDefects.aircraft}
                      aircraftTailNumber={deferredDefects.aircraft}
                      aircraftUuid={acft.aircraftUuid}
                      SystemData={SystemData}
                      classes={classes}
                      onChange={() => {
                        setDeferredDefects({showDeferredDefects: false, aircraft: ""});
                      }}
                    />
                    : <></>
                  }
                </Card>
              </Grid>
            )
          })
        }
      </Grid>
    )
  }
  else
    return (<></>)
}

export default AircraftGrid;
